import React, { useState } from 'react';
import { Select, Input, Button, Space, message } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import type { SelectProps } from 'antd';
import type { DefaultOptionType } from 'antd/es/select';

interface CreatableSelectProps extends Omit<SelectProps, 'dropdownRender'> {
  onCreateNew?: (value: string) => Promise<{ id: number; name: string }>;
  onOptionsUpdate?: (newOption: { value: number; label: string }) => void;
  createText?: string; // Custom placeholder for the create input
}

/**
 * CreatableSelect Component
 * 
 * Select component that allows creating new options on the fly
 * 
 * Features:
 * - Standard select dropdown with existing options
 * - Input field at the bottom to add new options
 * - Calls onCreateNew callback to create the option in the backend
 * - Automatically updates the options list and selects the newly created option
 */
export const CreatableSelect: React.FC<CreatableSelectProps> = ({
  onCreateNew,
  onOptionsUpdate,
  options = [],
  createText = "Nuevo cargo",
  ...selectProps
}) => {
  const [newItemName, setNewItemName] = useState('');
  const [creating, setCreating] = useState(false);
  const [localOptions, setLocalOptions] = useState<DefaultOptionType[]>(options as DefaultOptionType[]);

  // Update local options when props change
  React.useEffect(() => {
    setLocalOptions(options as DefaultOptionType[]);
  }, [options]);

  const handleCreate = async () => {
    if (!newItemName.trim()) {
      message.warning('Por favor ingrese un nombre');
      return;
    }

    if (!onCreateNew) {
      return;
    }

    setCreating(true);
    try {
      const newItem = await onCreateNew(newItemName.trim());
      
      // Create new option
      const newOption: DefaultOptionType = {
        value: newItem.id,
        label: newItem.name,
      };
      
      // Update local options list
      const updatedOptions = [...localOptions, newOption];
      setLocalOptions(updatedOptions);
      
      // Notify parent component
      if (onOptionsUpdate) {
        onOptionsUpdate({ value: newItem.id, label: newItem.name });
      }
      
      // Clear input
      setNewItemName('');

      // Select the newly created item. For multi-select / tags we
      // must emit an array (existing value + new id), otherwise the
      // form ends up with a bare integer and the backend rejects
      // ManyToMany fields like `packers` / `exporters` with
      // "Se esperaba una lista".
      if (selectProps.onChange) {
        const mode = selectProps.mode;
        if (mode === 'multiple' || mode === 'tags') {
          const current = Array.isArray(selectProps.value)
            ? (selectProps.value as Array<number | string>)
            : [];
          const nextValue = [...current, newItem.id];
          const nextOptions: DefaultOptionType[] = nextValue.map((v) =>
            v === newItem.id
              ? newOption
              : (updatedOptions.find((o) => o.value === v) as DefaultOptionType | undefined) ?? {
                  value: v as any,
                  label: String(v),
                },
          );
          selectProps.onChange(nextValue, nextOptions);
        } else {
          selectProps.onChange(newItem.id, newOption);
        }
      }

      message.success('Creado exitosamente');
    } catch (error: any) {
      console.error('Error creating new item:', error);
      message.error(error.message || 'Error al crear');
    } finally {
      setCreating(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleCreate();
    }
  };

  return (
    <Select
      {...selectProps}
      options={localOptions}
      dropdownRender={(menu) => (
        <>
          {menu}
          {onCreateNew && (
            <>
              <div style={{ borderTop: '1px solid #d9d9d9', margin: '4px 0' }} />
              <Space style={{ padding: '8px', width: '100%' }}>
                <Input
                  placeholder={createText}
                  value={newItemName}
                  onChange={(e) => setNewItemName(e.target.value)}
                  onKeyPress={handleKeyPress}
                  disabled={creating}
                  style={{ flex: 1 }}
                />
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleCreate}
                  loading={creating}
                  size="small"
                  shape="circle"
                />
              </Space>
            </>
          )}
        </>
      )}
    />
  );
};

export default CreatableSelect;
